import React, {useState, useEffect, useRef}  from 'react'
/** @jsxImportSource @emotion/react */
import {css} from '@emotion/react'

import useMediaQuery from '@mui/material/useMediaQuery'
import DOMPurify from 'dompurify'

const cssStyles = ({lineCount}) => ({
  wrapper: css`
    overflow: hidden;
    transition: max-height 300ms ease;

    button {
      display: none;
    }

    a {
      text-decoration: none;
      color: rgb(62, 166, 255);
    }
  `,
  text: css`
    width: 100%;
    letter-spacing: -0.02rem;
    line-height: 1.5;
    margin: 0;

    ul {
      margin: 0;
      white-space: normal;
    }

    p:not(ul p):not(li p) {
      margin-bottom: 16px;
    }
  `,
  expandButton: css`
    font-size: 0.9rem;
    color: rgb(62, 166, 255);
    cursor: pointer;
    margin-top: 10px;
    font-weight: 600;
    text-transform: uppercase;
  `
})

const isHTML = text => /<\/?[a-z][\s\S]*>/i.test(text)

DOMPurify.addHook('afterSanitizeAttributes', node => {
  if (node.tagName === 'A') {
    node.setAttribute('target', '_blank')
    node.setAttribute('rel', 'noopener noreferrer')
  }
})


const ExpandingText = ({ text, lineCount: initialLineCount, ...rest }) => {
  const ref = useRef()
  const [isTruncated, setIsTruncated] = useState(true)
  const [heights, setHeights] = useState({ collapsed: 0, expanded: 0 })
  const [isShowingButton, setIsShowingButton] = useState(false)

  const isSmallScreen = useMediaQuery(theme => theme.breakpoints.down('lg'))
  const lineCount = initialLineCount || (isSmallScreen ? 7 : 15)
  const styles = cssStyles({ lineCount })

  // strip out children so we don’t accidentally pass it to the HTML branch
  const { children: _ignore, ...filteredRest } = rest

  // Measure collapsed vs expanded heights
  useEffect(() => {
    if (!ref.current) return
    const node = ref.current
    const lh = parseFloat(getComputedStyle(node).lineHeight)
    const collapsed = lh * lineCount

    // temporarily remove max-height so we can read the full scrollHeight
    node.style.maxHeight = 'none'
    const expanded = node.scrollHeight
    node.style.maxHeight = ''

    setHeights({ collapsed, expanded })
    setIsShowingButton(expanded > collapsed + 5)
  }, [text, lineCount])

  const toggleExpand = () => setIsTruncated(t => !t)

  const wrapperStyle = {
    maxHeight: isTruncated ? heights.collapsed : heights.expanded
  }

  return (
    <div>
      {isHTML(text) ? (
        <div
          ref={ref}
          css={[styles.wrapper, styles.text]}
          style={wrapperStyle}
          // only spread filteredRest here, so no `children` prop
          {...filteredRest}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(text) }}
        />
      ) : (
        <div
          ref={ref}
          css={[styles.wrapper, styles.text]}
          style={wrapperStyle}
          // safe to spread the rest of the props here too
          {...filteredRest}
        >
          {text}
        </div>
      )}

      {isShowingButton && (
        <p css={styles.expandButton} onClick={toggleExpand}>
          {isTruncated ? 'Expand' : 'Collapse'}
        </p>
      )}
    </div>
  )
}

export default ExpandingText

